import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MapPin, Mail, Phone, Clock, LogIn, ArrowLeft, Copy, Check } from "lucide-react";
import logo from "assets/images/potentialogo.png";

export default function ContactUs() {
  const navigate = useNavigate();
  const location = useLocation();

  const fromTeacher =
    location.state?.from === "teacher" ||
    sessionStorage.getItem("fromTeacher") === "1";

  const address =
    "Jl. Permata Sari No.1000, Binong, Kec. Curug, Kabupaten Tangerang, Banten 15810";
  const encodedAddress = encodeURIComponent(address);
  const mapSrc = `https://www.google.com/maps?&q=${encodedAddress}&z=16&output=embed`;

  const whatsappNumber = "6282312121660";
  const whatsappMessage = encodeURIComponent("Hello, I would like to ask about Potentia.");
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const [copied, setCopied] = useState(false);

  const isLoggedIn = Boolean(
    sessionStorage.getItem("idToken") || localStorage.getItem("idToken")
  );

  const goBack = () => {
    const canGoBack = window.history.length > 1;

    if (!isLoggedIn) {
      // Likely came from Login.jsx — don't go back, go to "/" instead.
      navigate("/", { replace: true });
      return;
    }

    if (canGoBack) {
      navigate(-1);
    } else {
      // Logged in but no history entry (e.g., opened directly)
      navigate("/teacher", { replace: true });
    }
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {}
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50 relative overflow-hidden pb-24 md:pb-0"
      style={{
        paddingTop: "max(env(safe-area-inset-top), 0px)",
        paddingBottom: "max(env(safe-area-inset-bottom), 0px)",
      }}
    >
      {/* Back button — now ALWAYS visible */}
      <button
        onClick={goBack}
        className={`fixed top-4 left-4 sm:top-6 sm:left-6 z-50 flex items-center gap-2 bg-white/90 backdrop-blur border border-slate-200 text-slate-700 hover:text-slate-900 hover:bg-white shadow-sm hover:shadow px-3 py-2 rounded-full transition
          ${fromTeacher ? "" : ""}`}
        title="Kembali"
        aria-label="Kembali"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="hidden sm:inline text-sm font-medium">Kembali</span>
      </button>

      {/* Sign-In (only show if we didn't come from teacher page) */}
      {!fromTeacher && (
        <Link
          to="/"
          className="fixed top-4 right-4 sm:top-6 sm:right-8 z-50 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.04] active:scale-[0.97] rounded-full group"
          title="Sign In"
          aria-label="Sign In"
          style={{ paddingRight: 0 }}
        >
          <span className="hidden sm:inline px-5 py-2.5 font-semibold text-sm rounded-full">Masuk</span>
          <span className="sm:hidden p-3">
            <LogIn className="w-5 h-5 text-white group-hover:rotate-12 transition-transform" />
          </span>
        </Link>
      )}

      {/* Background blobs */}
      <div className="absolute top-[-10rem] left-[-6rem] w-[26rem] h-[26rem] bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-35 motion-safe:animate-blob" />
      <div className="absolute top-[-6rem] right-[-6rem] w-[26rem] h-[26rem] bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-35 motion-safe:animate-blob animation-delay-2000" />
      <div className="absolute bottom-[-10rem] left-1/3 w-[26rem] h-[26rem] bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-35 motion-safe:animate-blob animation-delay-4000" />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-7 sm:mb-9">
          <div className="relative mx-auto inline-flex max-w-[560px]">
            <div className="p-[2px] rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 shadow-[0_10px_28px_-14px_rgba(59,130,246,0.45)]">
              <div className="rounded-full bg-white/90 backdrop-blur-xl ring-1 ring-white/70 px-8 sm:px-10 md:px-12 py-2.5 sm:py-3 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full pointer-events-none bg-[radial-gradient(120%_120%_at_30%_10%,rgba(59,130,246,0.06),rgba(236,72,153,0.06)_60%,transparent_70%)]" />
                <img
                  src={logo}
                  alt="Potentia"
                  className="relative z-[1] h-10 sm:h-12 md:h-14 w-auto object-contain drop-shadow-sm"
                />
              </div>
            </div>
            <div className="absolute inset-0 -z-10 blur-xl opacity-25 bg-gradient-to-r from-blue-300 via-purple-300 to-pink-300 rounded-full" />
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-5">
            Hubungi Kami
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mt-2 px-2">
            Kami siap membantu dan menjawab setiap pertanyaan yang Anda miliki.
          </p>
        </div>

        {/* Contact Info + Map */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-8 items-stretch">
          {/* Info Card */}
          <div className="relative">
            <div className="absolute inset-0 -m-[1.5px] rounded-3xl bg-gradient-to-br from-slate-100 via-slate-200 to-slate-100 opacity-80 pointer-events-none" />
            <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-xl/50 shadow-slate-200 border border-slate-100/70 h-full">
              <div className="h-1.5 sm:h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-t-2xl sm:rounded-t-3xl" />
              <div className="p-6 sm:p-8 md:p-10 flex flex-col justify-between h-full">
                <div className="space-y-6 sm:space-y-7">
                  {/* Address */}
                  <section className="flex items-start gap-3 sm:gap-4 group">
                    <div className="shrink-0 p-2.5 sm:p-3 rounded-xl bg-blue-50 group-hover:bg-blue-100 transition-colors ring-1 ring-blue-100">
                      <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 mb-1">Alamat</h3>
                        <button
                          type="button"
                          onClick={copyAddress}
                          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 rounded-md px-2 py-1 border border-slate-200 hover:border-slate-300 transition"
                          aria-label="Salin alamat"
                          title="Salin alamat"
                        >
                          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copied ? "Disalin" : "Salin"}
                        </button>
                      </div>
                      <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                        <strong>Heartline Center</strong>
                        <br />
                        Gd. Heartline Center JL. Permatasari No. 1000. Lippo Village Tangerang 15811, Lantai 3.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium shadow-sm active:scale-95 transition"
                        >
                          Buka di Google Maps
                        </a>
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center px-3 py-2 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg font-medium border border-blue-200 active:scale-95 transition"
                        >
                          Dapatkan Arah
                        </a>
                      </div>
                    </div>
                  </section>

                  {/* Email */}
                  <section className="flex items-start gap-3 sm:gap-4 group">
                    <div className="shrink-0 p-2.5 sm:p-3 rounded-xl bg-green-50 group-hover:bg-green-100 transition-colors ring-1 ring-green-100">
                      <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                      <a
                        href="mailto:admin@deushcs.com"
                        className="block text-slate-600 hover:text-blue-600 transition-colors break-all"
                      >
                        admin@deushcs.com
                      </a>
                    </div>
                  </section>

                  {/* Phone */}
                  <section className="flex items-start gap-3 sm:gap-4 group">
                    <div className="shrink-0 p-2.5 sm:p-3 rounded-xl bg-purple-50 group-hover:bg-purple-100 transition-colors ring-1 ring-purple-100">
                      <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">No Telepon</h3>
                      <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-block text-slate-600 hover:text-blue-600 transition-colors"
                      >
                        +62 823-1212-1660 (WhatsApp)
                      </a>
                      <div className="mt-1">
                        <a href="tel:+6282312121660" className="text-sm text-slate-500 underline hover:text-slate-700">
                          Panggil langsung
                        </a>
                      </div>
                    </div>
                  </section>

                  {/* Hours */}
                  <section className="flex items-start gap-3 sm:gap-4 group">
                    <div className="shrink-0 p-2.5 sm:p-3 rounded-xl bg-orange-50 group-hover:bg-orange-100 transition-colors ring-1 ring-orange-100">
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Jam Kerja</h3>
                      <div className="text-slate-600 space-y-1 text-sm sm:text-base">
                        <p>Senin – Jumat: 09:00 – 17:00</p>
                        <p>Sabtu - Minggu: Tutup</p>
                        <p className="text-xs sm:text-sm text-slate-500 mt-2">WIB (UTC+7)</p>
                      </div>
                    </div>
                  </section>
                </div>

                {/* Privacy Text */}
                <div className="pt-6 mt-8 border-t border-slate-100">
                  <p className="text-xs sm:text-sm text-slate-500 text-center px-2 leading-relaxed">
                    Dengan menghubungi kami, Anda menyetujui{" "}
                    <a href="/terms" className="underline hover:text-slate-700 transition-colors">
                      Ketentuan Layanan
                    </a>{" "}
                    dan{" "}
                    <a href="/privacy" className="underline hover:text-slate-700 transition-colors">
                      Kebijakan Privasi
                    </a>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Map Card — now flex so the map can grow to full height */}
          <div className="relative">
            <div className="absolute inset-0 -m-[1.5px] rounded-3xl bg-gradient-to-br from-slate-100 via-slate-200 to-slate-100 opacity-80 pointer-events-none" />
            <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-xl/50 shadow-slate-200 border border-slate-100/70 overflow-hidden h-full flex">
              {/* Wrapper fills card height, but keeps a sane minimum to avoid collapse */}
              <div className="relative flex-1 h-full min-h-[360px] sm:min-h-[400px] md:min-h-[420px] lg:min-h-[500px]">
                <iframe
                  src={mapSrc}
                  className="absolute inset-0 w-full h-full"
                  style={{ border: 0, display: "block" }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Notice */}
        <div className="mt-8 sm:mt-10 bg-white rounded-2xl shadow-sm border border-slate-100 p-5 sm:p-6 text-center">
          <p className="text-slate-700 text-sm sm:text-base">
            <span className="font-semibold">Butuh bantuan?</span> Tim kami akan merespon secepatnya pada jam kerja.
          </p>
        </div>
      </div>

      {/* Sticky mobile CTA */}
      <div
        className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 8px)" }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3">
          <a
            href={whatsappLink}
            target="_blank"
            rel="noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-white bg-emerald-600 hover:bg-emerald-700 font-semibold shadow active:scale-95 transition"
          >
            <Phone className="w-5 h-5 text-white" />
            Chat di WhatsApp
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative text-center py-5 text-xs sm:text-sm text-slate-600">
        © {new Date().getFullYear()} Deus Human Capital Services | Potentia
      </footer>

      {/* Page styles */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.08); }
          66% { transform: translate(-20px, 20px) scale(0.95); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .motion-safe\\:animate-blob { animation: blob 9s infinite; }
        @media (prefers-reduced-motion: reduce) {
          .motion-safe\\:animate-blob { animation: none; }
        }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .shadow-xl\\/50 { box-shadow: 0 25px 50px -12px rgba(30, 41, 59, 0.12); }
      `}</style>
    </div>
  );
}
